// src/components/learning/CourseInterview.jsx
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ChevronDown, 
  ChevronUp,
  MessageCircle,
  Lightbulb 
} from 'lucide-react';
import { courseApi } from '../../services/courseApi';
import { motion, AnimatePresence } from 'framer-motion';

const CourseInterview = () => {
  const { courseId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        const response = await courseApi.getInterviewQuestions(courseId);
        if (response.success) {
          setQuestions(response.data.questions || []);
        } else {
          throw new Error(response.error || 'Failed to load interview questions');
        }
      } catch (error) {
        console.error('Error loading interview questions:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-center">
        <div className="space-y-4">
          <p className="text-red-400 text-xl">{error}</p>
          <Link to={`/learning/courses/${courseId}`} className="text-blue-400 hover:text-blue-300">
            Back to Course
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <Link 
          to={`/learning/courses/${courseId}`}
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Course
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">Interview Questions</h1>
          <p className="text-gray-400">
            Practice with these common interview questions to prepare for your next opportunity.
          </p>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {questions.map((question, index) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-800/50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setExpandedId(expandedId === question.id ? null : question.id)}
                className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400 font-medium">
                    {index + 1}
                  </div>
                  <h3 className="text-white font-medium pr-8">
                    {question.question}
                  </h3>
                </div>
                {expandedId === question.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              <AnimatePresence>
                {expandedId === question.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4"
                  >
                    <div className="pl-12">
                      {/* Answer */}
                      <div className="prose prose-invert max-w-none">
                        {question.answer}
                      </div>

                      {/* Tips */}
                      {question.tips && (
                        <div className="mt-4 bg-blue-500/10 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-blue-400 mb-2">
                            <Lightbulb className="w-5 h-5" />
                            <h4 className="font-medium">Tips</h4>
                          </div>
                          <ul className="list-disc list-inside text-gray-300 space-y-1">
                            {question.tips.map((tip, tipIndex) => (
                              <li key={tipIndex}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Resources */}
                      {question.resources && (
                        <div className="mt-4">
                          <h4 className="text-white font-medium mb-2">Additional Resources:</h4>
                          <ul className="space-y-2">
                            {question.resources.map((resource, resourceIndex) => (
                              <li key={resourceIndex}>
                                <a
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                  {resource.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CourseInterview;